/**
 * 素材管理：角色立繪、NPC 站立圖、3D 模型、音效。
 *
 * 規則：
 * 1. 所有素材放在 public/assets/ 底下，路徑寫在 content JSON（例如 npc.portrait）
 * 2. build 時 scripts/build-asset-manifest.ts 產生 public/assets/manifest.json
 * 3. 執行期只載入 manifest 裡「真的存在」的檔案；不存在就用程式生成的替代品
 *    → 美術可以一張一張補圖，遊戲永遠不會因缺圖壞掉、也不會有 404 噪音
 */
import * as THREE from 'three';
import { prefetchManifest } from './manifestPrefetch';
export { prefetchManifest };

const BASE = `${import.meta.env.BASE_URL}assets/`;
let manifest: Set<string> | null = null;

export async function initAssets(): Promise<void> {
  manifest = await prefetchManifest();
}
export const hasAsset = (path?: string | null): boolean => !!path && !!manifest?.has(path);
export const assetUrl = (path: string) => BASE + path;

/** 依表情找立繪：characters/{id}/portrait_{expression}.webp → portrait_neutral → null */
export function portraitUrl(portrait: string, expression = 'neutral'): string | null {
  const variant = portrait.replace(/neutral(\.(webp|png|jpg))$/, `${expression}$1`);
  if (hasAsset(variant)) return assetUrl(variant);
  if (hasAsset(portrait)) return assetUrl(portrait);
  return null;
}

/** 沒有立繪時的替代頭像（SVG，姓名首字＋代表色） */
export function fallbackAvatar(name: string, color: string): string {
  const ch = [...name.replace(/[（(].*$/, '')].slice(-1)[0] ?? '?';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96"><rect width="96" height="96" rx="20" fill="${color}"/>`
    + `<circle cx="48" cy="40" r="20" fill="#f1d3b6"/><path d="M28 38 Q48 10 68 38 Z" fill="#f4f6f6"/>`
    + `<rect x="36" y="44" width="24" height="9" rx="3" fill="#dfe8ea"/>`
    + `<path d="M16 96 Q48 58 80 96 Z" fill="#f4f6f6"/>`
    + `<text x="80" y="88" font-size="24" font-weight="900" text-anchor="middle" fill="#1a2630" font-family="sans-serif">${ch}</text></svg>`;
  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
}

const texLoader = new THREE.TextureLoader();
/** NPC 2D 站立圖（billboard）。有圖就回傳 Sprite，沒有回傳 null 讓呼叫端用程式生成的人物 */
export function npcSprite(path?: string): THREE.Sprite | null {
  if (!hasAsset(path)) return null;
  const tex = texLoader.load(assetUrl(path!)); tex.colorSpace = THREE.SRGBColorSpace;
  const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true }));
  s.scale.set(1.1, 1.9, 1); s.position.y = 0.95; return s;
}

let gltfLoader: any = null;
/** 3D 模型（.glb）。延遲載入 GLTFLoader，沒有用到模型的版本不會多下載這段程式 */
export async function loadModel(path?: string): Promise<THREE.Object3D | null> {
  if (!hasAsset(path)) return null;
  if (!gltfLoader) { const m = await import('three/examples/jsm/loaders/GLTFLoader.js'); gltfLoader = new m.GLTFLoader(); }
  return new Promise(resolve => gltfLoader.load(assetUrl(path!), (g: any) => resolve(g.scene), undefined, () => resolve(null)));
}

/**
 * v1.4 新版對話卡的半身立繪（600×800 PNG，透明背景）。檔名規則見 docs/PORTRAITS.md：
 *   沒穿工作服（便服／辦公服／訪客）：portraits/npc/<NPC id>_<表情>.png
 *   穿工作服（口罩＋網帽，只露眼睛，大家長一樣，只差帽子）：portraits/uniform/white-<帽子>_<表情>.png、gray_<表情>.png、green_<表情>.png
 * 表情：neutral／happy／worried／angry。找不到該表情就用 neutral；都沒有回傳 null（用原本的小頭像）。
 */
export const PORTRAIT_EXPRESSIONS = ['neutral', 'happy', 'worried', 'angry'] as const;
export function uniformPortraitKey(uniform: string, hat: string): string | null {
  if (uniform === 'white') return `white-${hat === 'none' ? 'plain' : hat}`;
  if (uniform === 'gray' || uniform === 'green') return uniform;
  return null;
}
export function bustUrl(npc: { id: string; uniform: string; hat: string }, expression = 'neutral', inProduction = false): string | null {
  const ukey = inProduction ? uniformPortraitKey(npc.uniform, npc.hat) : null;
  const bases = [...(ukey ? [`portraits/uniform/${ukey}`] : []), `portraits/npc/${npc.id}`];
  for (const b of bases) for (const ex of [expression, 'neutral']) { const p = `${b}_${ex}.png`; if (hasAsset(p)) return assetUrl(p); }
  return null;
}
